// Move DOM focus into the freshly-opened ContextDropdown panel.
//
// WHY IT EXISTS (INTERACT-1, 2026-08-14). The shared ContextDropdown closes on
// Escape from `Menu.onKeydown`, a DELEGATED handler — smbls' delegate walks the
// DOM ancestor chain of `event.target` (smbls/packages/element/src/delegate.js
// installDelegatedEvent), so a keystroke never reaches it unless focus actually
// moved into the panel. Nothing moves focus there on its own: a right-click
// leaves focus wherever it was. This function is that nudge.
//
// Bounded poll, not a single microtask check: the panel does not exist in the
// DOM until the opener's render commits and the previously-`display:none`
// panel paints — the same shape as `ContextDropdownItem.scope.openSubmenu`'s
// own `focusFirst` option polling for its submenu's first row.
//
// Scoped to `[data-key="ContextDropdown"]` (DOMQL stamps every element's OWN
// key as `data-key` — smbls/packages/element/src/create.js) rather than a bare
// `[role="menuitem"]` query: ContextDropdown is a single global singleton so
// this is normally unambiguous either way, but naming its subtree precisely
// means it can never accidentally grab a row from an unrelated open menu (e.g.
// an `@symbo.ls/dropdown` DropdownRoot panel, a different mechanism used
// elsewhere in this app).
//
// ── WHY THE NAME IS NEUTRAL ───────────────────────────────────────────────
// This shipped as `focusHomeContextMenu` inside functions/homeContextMenu.js,
// because Home's three openers were the first callers. The panel it focuses is
// the SHELL-WIDE `root.contextDropdown` singleton, though — every surface that
// opens one needs the identical nudge, and an installed workspace MODULE
// already calls it from its own pages (VaultOS 1.38.1, measured 2026-09-10).
// A Home-scoped name on a shell-wide affordance reads as "this only works on
// Home", which was never true, and it makes a module author guess.
//
// `focusHomeContextMenu` stays exported from functions/homeContextMenu.js (and
// from functions/index.js) as a thin alias forwarding `this` + arguments here,
// so every existing caller — the three Home openers in pages/main.js, any
// installed module that already wrote `el.call('focusHomeContextMenu')` —
// keeps working with no change on their side.
export const focusContextMenu = () => {
  if (typeof document === 'undefined') return
  let tries = 0
  const tick = () => {
    const first = document.querySelector('[data-key="ContextDropdown"] [role="menuitem"]')
    if (first && typeof first.focus === 'function') {
      first.focus()
      return
    }
    if (++tries < 8) window.setTimeout(tick, 30)
  }
  window.setTimeout(tick, 0)
}
